import * as React from "react";
import { useState, useEffect } from "react";
import { Headings } from "@arctheme-components/elements/Headings/Headings";
import { Paragraph } from "@arctheme-components/elements/Paragraph/Paragraph";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";

type ImageBlock = {
    type: string;
    settings: {
        image?: string;
        mobile_image?: string;
        image_position?: string;
        overlay_opacity?: number;
        overlay_color?: string;
    };
};

type Tab = {
    title: string;
    link: string;
};

type SectionSettings = {
    content_alignment_desktop?: string;
    content_alignment_mobile?: string;
    heading_type_mobile?: string;
    heading_type_desktop?: string;
    content_vertical_position_desktop?: string;
    content_vertical_position_mobile?: string;
    layout_type?: string;
    mobile_min_height?: number;
    desktop_min_height?: number;
    title_margin_bottom_mobile?: number;
    title_margin_bottom_desktop?: number;
    title?: string;
    show_description?: boolean;
    body_type?: string;
    content_width?: string | number;
};

type Settings = {
    blocks?: ImageBlock[];
    section: SectionSettings;
    description?: string;
    globals?: {
        collection_tabs?: Tab[];
    };
};

interface CollectionHeaderProps {
    settings: Settings;
}

export const CollectionHeader: React.FC<CollectionHeaderProps> = ({ settings }) => {
    const isMobile = useMobileBreakpoint('1024px');
    const [isSticky, setIsSticky] = useState(false);
    const headerRef = React.useRef<HTMLDivElement>(null);
    const [rightOffset, setRightOffset] = useState(0);
    
    // Find background image block if available
    const imageBlock = settings.blocks?.find(block => block.type === 'image');
    // Get content alignment values
    const contentAlignmentDesktop = settings.section.content_alignment_desktop || 'center';
    const contentAlignmentMobile = settings.section.content_alignment_mobile || 'center';
    
    // Get correct heading type based on device
    const headingType = isMobile 
        ? settings.section.heading_type_mobile || 'h3' 
        : settings.section.heading_type_desktop || 'h2';
    
    // Get vertical positioning
    const verticalPositionDesktop = settings.section.content_vertical_position_desktop || 'center';
    const verticalPositionMobile = settings.section.content_vertical_position_mobile || 'center';

    const tabs: Tab[] = Array.isArray(settings?.globals?.collection_tabs)
        ? settings?.globals?.collection_tabs.filter(tab => tab.title && tab.link)
        : [];

    const currentHandle = typeof window !== "undefined"
        ? window.location.pathname.split('/').filter(Boolean).pop() || ""
        : "";

    const isCurrentInTabs = tabs.some(tab =>
        tab.link.split('/').filter(Boolean).pop() === currentHandle
    );

    const isActiveTab = (link: string) => {
        const currentLink = link.split('/').filter(Boolean).pop() || "";
        return currentHandle.toLowerCase().includes(currentLink.toLowerCase());
    };

    useEffect(() => {
        const handleScroll = () => {
            const headerElement = document.querySelector('.header-container') as HTMLElement | null;
            const containerElement = headerElement?.querySelector('.container') as HTMLElement | null;
           
            if (headerElement && containerElement) {
                const headerHeight = headerElement.offsetHeight;
                const stickyTop = headerHeight + 20;
                const containerWidth = containerElement.offsetWidth;
                const windowWidth = window.innerWidth;
                const rightOffset = (windowWidth - containerWidth) / 2;

                setIsSticky(window.scrollY > stickyTop);
                setRightOffset(rightOffset);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);


    if (settings.section.layout_type === 'image') {
        return (
            <div className="collection-header__with-image relative w-full h-full" 
                style={{
                    minHeight: isMobile 
                        ? `${settings.section.mobile_min_height || 180}px` 
                        : `${settings.section.desktop_min_height || 300}px`
                }}>
                {/* Background Image */}
                {imageBlock && 
                    <div className="collection-header__background absolute inset-0 w-full h-full overflow-hidden">
                        <picture className="w-full h-full block">
                            <source
                                media="(max-width: 767px)"
                                srcSet={imageBlock.settings.mobile_image || imageBlock.settings.image}
                            />
                            <img
                                src={imageBlock.settings.image}
                                className="w-full h-full object-cover"
                                style={{ objectPosition: imageBlock.settings.image_position || 'center center' }}
                                alt=""
                            />
                        </picture>
                        
                        {/* Overlay if specified */}
                        {imageBlock.settings.overlay_opacity > 0 && (
                            <div 
                                className="absolute inset-0 w-full h-full" 
                                style={{
                                    backgroundColor: imageBlock.settings.overlay_color || '#000000',
                                    opacity: imageBlock.settings.overlay_opacity / 100
                                }}
                            />
                        )}
                    </div>
                }
                
                {/* Content Container */}
                <div className={`
                    absolute top-0 left-0 w-full flex flex-col h-full
                    text-${contentAlignmentMobile} lg:text-${contentAlignmentDesktop}
                    justify-${verticalPositionMobile} lg:justify-${verticalPositionDesktop}
                    relative
                `}>
                    <div className={`mb-[${settings.section.title_margin_bottom_mobile}px] lg:mb-[${settings.section.title_margin_bottom_desktop}px]`}>
                        <Headings
                            text={settings.section.title}
                            type={headingType}
                            classes={`collection-header__with-image__title`}
                        />
                    </div>
                    
                    {settings.section.show_description && settings.description && (
                        <div className={`mx-auto text-${contentAlignmentMobile} lg:text-${contentAlignmentDesktop}`} 
                            style={{maxWidth: settings.section.content_width || '600px'}}>
                            <Paragraph
                                text={settings.description}
                                type={settings.section.body_type || 'b2'}
                            />
                        </div>
                    )}
                </div>
            </div>
        )
    } else {
        return (
            <div className={`
                collection-header-without-image flex flex-col h-full
                text-${contentAlignmentMobile} lg:text-${contentAlignmentDesktop}
                justify-${verticalPositionMobile} lg:justify-${verticalPositionDesktop}
            `}
            style={{
                minHeight: isMobile 
                    ? `${settings.section.mobile_min_height || 120}px` 
                    : `${settings.section.desktop_min_height || 200}px`
            }}>
                  <div className="container relative"> 
                    <div className="flex justify-between flex-col lg:flex-row items-center gap-4 lg:gap-0">  
                        <div className={`mb-[${settings.section.title_margin_bottom_mobile}px] w-full text-${contentAlignmentMobile} lg:text-${contentAlignmentDesktop} lg:mb-[${settings.section.title_margin_bottom_desktop}px]`}>
                            <Headings
                                text={settings.section.title}
                                type="h1"
                                classes={`${headingType} collection-header-without-image__title after:content-[''] after:hidden`}
                            />
                        </div> 
                        {tabs.length > 0 && isCurrentInTabs && (
                     <div className={`collection-header-pill flex gap-[6px] transition-all duration-200 w-full lg:justify-end ${
                        isSticky ? 'fixed bg-transparent z-40 justify-center lg:justify-end px-[20px] lg:px-0 ' : ''
                        }`} style={{
                            top: isSticky ? `${document.querySelector('.header-container')?.offsetHeight + 20}px` : 'auto',
                            ...(isSticky && !isMobile && {
                                right: `${rightOffset}px`
                            }),
                            ...(isSticky && {
                                width: '100%'
                            })
                        }}>
                            <div className={`flex bg-white w-full gap-[6px] border-black border rounded-full p-1 justify-end lg:w-fit  ${
                                isSticky ? 'shadow-[0px_12px_16px_-4px_rgba(0,0,0,0.20),0px_4px_6px_-2px_rgba(0,0,0,0.03)]' : ''
                            }`}>
                                {tabs.map((tab, index) => (
                                    <a
                                        key={index}
                                        href={tab.link}
                                        className={`min-w-[104.2px] w-full lg:min-w-[126px] text-center flex items-center justify-center h-[50px] rounded-full transition-all duration-200 b3--bold ${
                                            isActiveTab(tab?.link) 
                                                ? 'border border-black bg-black text-white' 
                                                : 'hover:bg-black hover:text-white'
                                        }`}
                                    >
                                        {tab.title}
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                    </div>
                </div>
                
                {settings.section.show_description && settings.description && (
                    <div className={`mx-auto text-${contentAlignmentMobile} lg:text-${contentAlignmentDesktop}`} 
                        style={{maxWidth: settings.section.content_width || '600px'}}>
                        <Paragraph
                            text={settings.description}
                            type={settings.section.body_type || 'b2'}
                        />
                    </div>
                )}
            </div>
        )
    }
};

export default CollectionHeader;